/* An FAQ answer as the editor shows it, and back again.

   The stored answer stays Markdown — the client's site renders it with
   markdown-it, the Worker reads its links out of it to check them against the
   sitemap, and the Vault reads it as text. So the formatted editor is a view
   over Markdown, not a switch to HTML: what it can produce is exactly what
   these two functions round-trip — paragraphs, line breaks, **bold**, *italic*,
   `[links](/page)`, and flat bulleted or numbered lists. No headings, on
   purpose: an answer sits under its question's heading on the page.

   Anything a person types that Markdown would read as formatting (a `*`, a
   line starting `1. `) is escaped on the way out, so the page shows what the
   editor showed. */

const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/* The links the Worker will accept (faqLinkProblem). Anything else is shown as
   its text rather than as a link — the editor must not hand the browser a
   `javascript:` href, whatever an old answer holds. */
const SAFE_HREF = /^(https?:\/\/|mailto:|tel:|\/(?!\/)|#)/i;

const LIST_UL = /^\s{0,3}[-*+]\s+(.*)$/;
const LIST_OL = /^\s{0,3}\d{1,9}[.)]\s+(.*)$/;

/** Inline Markdown → HTML: escapes, links, bold, italic, backslash escapes. */
function inline(md: string, linksAs: 'a' | 'span'): string {
  // Backslash escapes first, parked as placeholders so the patterns below skip them.
  const parked: string[] = [];
  let s = md.replace(/\\([\\`*_[\]()#+\-.!>~|])/g, (_, ch: string) => `\u0000${parked.push(ch) - 1}\u0000`);
  s = escHtml(s);
  s = s.replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, (_, text: string, href: string) => {
    const raw = href.replace(/&amp;/g, '&');
    if (!SAFE_HREF.test(raw)) return text;
    return linksAs === 'a'
      ? `<a href="${escHtml(raw)}">${text}</a>`
      : `<span class="faqp__alink" title="${escHtml(raw)}">${text}</span>`;
  });
  s = s.replace(/\*\*\*(?=\S)([\s\S]*?\S)\*\*\*/g, '<strong><em>$1</em></strong>');
  s = s.replace(/(\*\*|__)(?=\S)([\s\S]*?\S)\1/g, '<strong>$2</strong>');
  s = s.replace(/(^|[^*])\*(?=\S)([^*]*?\S)\*(?!\*)/g, '$1<em>$2</em>');
  s = s.replace(/(^|[^_\w])_(?=\S)([^_]*?\S)_(?![_\w])/g, '$1<em>$2</em>');
  return s.replace(/\u0000(\d+)\u0000/g, (_, i: string) => escHtml(parked[Number(i)]));
}

/** Markdown → HTML for the editor (`a`) or for showing a saved answer (`span`). */
export function markdownToHtml(md: string, linksAs: 'a' | 'span' = 'a'): string {
  const out: string[] = [];
  let para: string[] = [];
  let list: { tag: 'ul' | 'ol'; items: string[] } | null = null;
  const flushPara = () => {
    if (para.length) out.push(`<p>${inline(para.join('\n'), linksAs).replace(/\n/g, '<br>')}</p>`);
    para = [];
  };
  const flushList = () => {
    if (list) out.push(`<${list.tag}>${list.items.map((i) => `<li>${inline(i, linksAs).replace(/\n/g, '<br>')}</li>`).join('')}</${list.tag}>`);
    list = null;
  };
  for (const line of md.replace(/\r\n?/g, '\n').split('\n')) {
    const ul = LIST_UL.exec(line);
    const ol = ul ? null : LIST_OL.exec(line);
    if (ul || ol) {
      flushPara();
      const tag = ul ? 'ul' : 'ol';
      if (list && list.tag !== tag) flushList();
      if (!list) list = { tag, items: [] };
      list.items.push((ul ?? ol)![1]);
    } else if (!line.trim()) {
      flushPara(); flushList();
    } else if (list && /^\s{2,}\S/.test(line)) {
      // An indented line under a list item continues it.
      list.items[list.items.length - 1] += `\n${line.trim()}`;
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara(); flushList();
  return out.join('');
}

/* ---- HTML (what the person edited) → Markdown ----------------------------- */

/** Text that Markdown would otherwise read as formatting. */
function escText(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/([*[\]])/g, '\\$1').replace(/(^|\W)_|_(?=\W|$)/g, (m) => m.replace('_', '\\_'));
}

/* Emphasis markers must hug the words: `** bold**` is not bold in Markdown.
   The browser happily puts a space inside <strong>, so it moves outside. */
function wrap(inner: string, mark: string): string {
  const m = /^(\s*)([\s\S]*?)(\s*)$/.exec(inner)!;
  return m[2] ? `${m[1]}${mark}${m[2]}${mark}${m[3]}` : inner;
}

function inlineMd(node: Node): string {
  if (node.nodeType === Node.TEXT_NODE) return escText((node.textContent ?? '').replace(/\u00a0/g, ' ').replace(/\n/g, ' '));
  if (!(node instanceof Element)) return '';
  const kids = () => [...node.childNodes].map(inlineMd).join('');
  switch (node.tagName) {
    case 'BR': return '\n';
    case 'STRONG': case 'B': return wrap(kids(), '**');
    case 'EM': case 'I': return wrap(kids(), '*');
    case 'A': {
      const href = (node.getAttribute('href') ?? '').trim().replace(/ /g, '%20').replace(/\(/g, '%28').replace(/\)/g, '%29');
      // Brackets in the text would end the link early (as in insertLink).
      const text = kids().replace(/\\?[[\]]/g, '').trim();
      return href && SAFE_HREF.test(href) && text ? `[${text}](${href})` : kids();
    }
    default: return kids();
  }
}

/* A line that would read as a list item, or a stray blank line, once written:
   escaped so a typed "1. " or "- " stays text. */
const tidyLines = (s: string) => s.split('\n').map((l) => l.trim()).filter(Boolean)
  .map((l) => l.replace(/^(\d{1,9})([.)])(\s)/, '$1\\$2$3').replace(/^([-+])(\s)/, '\\$1$2'));

const BLOCK = /^(P|DIV|H[1-6]|UL|OL|LI|BLOCKQUOTE|PRE|SECTION|ARTICLE)$/;

/** The editor's DOM → Markdown. Headings and anything else block-level are
 *  kept as paragraphs; nested lists are flattened into their parent. */
export function htmlToMarkdown(root: Element): string {
  const blocks: string[] = [];
  let run: Node[] = [];
  const flushRun = () => {
    const lines = tidyLines(run.map(inlineMd).join(''));
    if (lines.length) blocks.push(lines.join('\n'));
    run = [];
  };
  const listItems = (list: Element, ordered: boolean, into: string[]) => {
    for (const li of list.children) {
      if (li.tagName !== 'LI') continue;
      const own: Node[] = [];
      const nested: Element[] = [];
      for (const k of li.childNodes) {
        if (k instanceof Element && /^(UL|OL)$/.test(k.tagName)) nested.push(k);
        else own.push(k);
      }
      const lines = tidyLines(own.map((k) => (k instanceof Element && BLOCK.test(k.tagName) ? `${inlineMd(k)}\n` : inlineMd(k))).join(''));
      if (lines.length) {
        const mark = ordered ? `${into.length + 1}. ` : '- ';
        into.push(mark + lines.join(`\n${' '.repeat(mark.length)}`));
      }
      for (const n of nested) listItems(n, ordered, into);
    }
  };
  const walk = (parent: Node) => {
    for (const n of parent.childNodes) {
      if (n instanceof Element && (n.tagName === 'UL' || n.tagName === 'OL')) {
        flushRun();
        const items: string[] = [];
        listItems(n, n.tagName === 'OL', items);
        if (items.length) blocks.push(items.join('\n'));
      } else if (n instanceof Element && BLOCK.test(n.tagName)) {
        flushRun();
        // A block that holds a list (a <div> around one) is walked, not flattened.
        if (n.querySelector('ul, ol, p, div')) walk(n);
        else { run = [n]; flushRun(); }
      } else {
        run.push(n);
      }
    }
  };
  walk(root);
  flushRun();
  return blocks.join('\n\n');
}
